import { apiWithEntityId, apiWithMSO } from 'services/api';
import { useStateValue } from '../..';
import {
  CREATE_PAYMENT_DEVICE,
  DELETE_PAYMENT_DEVICE,
  FETCH_PAYMENT_SOLUTIONS,
  UPDATE_PAYMENT_SOLUTIONS,
  GET_CARD_READER_CONFIG,
  GET_CARD_READER_VALUES,
  GET_DEFAULT_PROVIDER,
  GET_PAYMENT_DEVICE,
  RESET_CARD_READER_VALUES,
  RESET_DEVICE_CREATED,
  RESET_DEVICE_DELETED,
  RESET_DEVICE_STATUS,
  RESET_DEVICE_UPDATED,
  RESET_PAYMENT_DEVICE_LIST,
  RESET_SAVE_CARD_READER_VALUES,
  RESET_SAVE_DEFAULT_CARD_READER_VALUES,
  RESET_CREATE_DEFAULT_CONFIG,
  SAVE_CARD_READER_VALUES,
  SAVE_DEFAULT_CARD_READER_VALUES,
  SET_FILTERED_LIST,
  TEST_PAYMENT_DEVICE,
  UPDATE_PAYMENT_DEVICE,
  action,
  actionFailure,
  actionSuccess,
  GET_POS_LOCATIONS,
  GET_PAYMENT_STATUS,
  GET_MSOPERMISSIONS,
  FETCH_ALL_PAYMENT_SOLUTIONS,
  DELETE_PAYMENT_SOLUTIONS,
  FETCH_CHANNEL_CONFIGURATION,
  UPDATE_CHANNEL_CONFIGURATION,
  GET_DEFAULT_CONFIGURATIONS,
  CREATE_DEFAULT_CONFIGURATION,
  FETCH_COMPONENT_SDK_CONFIG,
  UPDATE_COMPONENT_SDK_CONFIG,
} from '../actions';
import { PAYMENT_CONFIG } from '../type';

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const usePaymentConfig = () => {
  const { state, dispatch } = useStateValue();
  const { paymentConfig } = state;

  const toFetchPaymentSolutions = async () => {
    dispatch(action(FETCH_PAYMENT_SOLUTIONS));
    try {
      const response = await apiWithEntityId.get(
        `/adminEntityProcessorConfiguration`
      );
      dispatch(actionSuccess(FETCH_PAYMENT_SOLUTIONS, response?.data));
    } catch (e) {
      dispatch(actionFailure(FETCH_PAYMENT_SOLUTIONS, e));
    }
  };

  const toFetchAllPaymentSolutions = async () => {
    dispatch(action(FETCH_ALL_PAYMENT_SOLUTIONS));
    try {
      const response = await apiWithEntityId.get(`/processors/all`);
      dispatch(actionSuccess(FETCH_ALL_PAYMENT_SOLUTIONS, response?.data));
    } catch (e) {
      dispatch(actionFailure(FETCH_ALL_PAYMENT_SOLUTIONS, e));
    }
  };

  const toUpdatePaymentSolutions = async (
    values: PAYMENT_CONFIG.IUpdatePaymentSolutions
  ): Promise<PAYMENT_CONFIG.IUpdatePaymentSolutionsAPIResponse | null> => {
    // Ensure function returns a valid type
    dispatch(action(UPDATE_PAYMENT_SOLUTIONS));

    try {
      const response = await apiWithEntityId.post(
        `/adminEntityProcessorConfiguration`,
        values
      );

      dispatch(actionSuccess(UPDATE_PAYMENT_SOLUTIONS, response.data));

      return response?.data as PAYMENT_CONFIG.IUpdatePaymentSolutionsAPIResponse;
    } catch (e: unknown) {
      dispatch(actionFailure(UPDATE_PAYMENT_SOLUTIONS, e));

      // Return structured error response that matches the expected type
      // This prevents unsafe type casting of error responses
      // Backend returns errors with structure: { message: string, data: string, errorRaw: {} }
      // where 'data' contains the detailed error message
      const error = e as {
        response?: {
          data?: { message?: string; data?: unknown; error?: string };
        };
        message?: string;
      };
      const errorData = error?.response?.data;
      const errorMessage =
        (typeof errorData?.data === 'string' ? errorData.data : null) ||
        errorData?.message ||
        errorData?.error ||
        error?.message ||
        'An error occurred';

      return {
        message: errorData?.message || 'Request failed',
        data: {
          entityId: '',
          processor: { processors: [] },
        },
        errorRaw: errorMessage,
      };
    }
  };

  const toDeletePaymentSolutions = async (
    values: PAYMENT_CONFIG.IUpdatePaymentSolutions
  ): Promise<PAYMENT_CONFIG.IUpdatePaymentSolutionsAPIResponse | null> => {
    // Ensure function returns a valid type
    dispatch(action(DELETE_PAYMENT_SOLUTIONS));

    try {
      const response = await apiWithEntityId.remove(
        `/adminEntityProcessorConfiguration`,
        values
      );

      dispatch(actionSuccess(DELETE_PAYMENT_SOLUTIONS, response.data));

      return response?.data as PAYMENT_CONFIG.IUpdatePaymentSolutionsAPIResponse;
    } catch (e) {
      dispatch(actionFailure(DELETE_PAYMENT_SOLUTIONS, e));

      return null;
    }
  };

  const toFetchCardReaderValues = async (id?: string) => {
    dispatch(action(GET_CARD_READER_VALUES));
    try {
      const url = id
        ? `/entityConfigurations?processorId=${id}&countPerPage=500`
        : `/entityConfigurations?countPerPage=500`;
      const response = await apiWithEntityId.get<{
        data?: PAYMENT_CONFIG.IGetCardReaderList;
      }>(url);
      dispatch(actionSuccess(GET_CARD_READER_VALUES, response?.data));
      return response?.data?.data;
    } catch (e) {
      dispatch(actionFailure(GET_CARD_READER_VALUES, e));
      return undefined;
    }
  };

  const toFetchDefaultProviders = async () => {
    dispatch(action(GET_DEFAULT_PROVIDER));
    try {
      const response = await apiWithEntityId.get(`/defaultProcessors`);
      dispatch(actionSuccess(GET_DEFAULT_PROVIDER, response?.data));
    } catch (e) {
      dispatch(actionFailure(GET_DEFAULT_PROVIDER, e));
    }
  };

  const toFetchDefaultConfigurations = async () => {
    dispatch(action(GET_DEFAULT_CONFIGURATIONS));
    try {
      const response = await apiWithEntityId.get(
        `/defaultEntityConfigurations`
      );
      dispatch(actionSuccess(GET_DEFAULT_CONFIGURATIONS, response?.data));
    } catch (e) {
      dispatch(actionFailure(GET_DEFAULT_CONFIGURATIONS, e));
    }
  };

  const toCreateDefaultConfiguration = async (
    values: PAYMENT_CONFIG.IDefaultConfiguration
  ) => {
    dispatch(action(CREATE_DEFAULT_CONFIGURATION));
    try {
      const response = await apiWithEntityId.post(
        `/defaultEntityConfigurations`,
        values
      );
      dispatch(actionSuccess(CREATE_DEFAULT_CONFIGURATION, response?.data));
      return response?.data;
    } catch (e) {
      dispatch(actionFailure(CREATE_DEFAULT_CONFIGURATION, e));
      throw e;
    }
  };

  const toCreatePaymentDeviceLocation = async (
    values:
      | PAYMENT_CONFIG.ICreatePaymentDeviceSimple
      | PAYMENT_CONFIG.ICreatePaymentDeviceFull
  ) => {
    dispatch(action(CREATE_PAYMENT_DEVICE));
    try {
      if (values.locationId === '') {
        values.locationId = undefined;
      }

      if (values.posLocationId === '') {
        values.posLocationId = undefined;
      }

      const response = await apiWithEntityId.post(
        `/paymentDeviceLocations`,
        values
      );

      // `post` returns the full AxiosResponse — the body is at response.data.
      // Detect upstream-failure-wrapped-in-2xx the same way the update path does.
      const body = response?.data as
        | { message?: string; data?: unknown; errorRaw?: unknown }
        | undefined;
      if (body?.errorRaw) {
        const errorMessage =
          body?.message ||
          (typeof body?.data === 'string' ? body.data : null) ||
          'Failed to create payment device';
        dispatch(
          actionFailure(CREATE_PAYMENT_DEVICE, { message: errorMessage })
        );
        return;
      }

      dispatch(actionSuccess(CREATE_PAYMENT_DEVICE, body));
    } catch (e) {
      const err = e as {
        response?: { data?: { message?: string; data?: unknown } };
        message?: string;
      };
      const errorData = err?.response?.data;
      const errorMessage =
        errorData?.message ||
        (typeof errorData?.data === 'string' ? errorData.data : null) ||
        err?.message ||
        'Failed to create payment device';
      dispatch(
        actionFailure(CREATE_PAYMENT_DEVICE, { message: errorMessage })
      );
    }
  };

  const saveGlobalCardReaderValues = async (
    values: PAYMENT_CONFIG.IGlobalCardReader
  ) => {
    dispatch(action(SAVE_CARD_READER_VALUES));
    try {
      const response = await apiWithEntityId.post(
        `/entityConfigurations`,
        values
      );
      dispatch(actionSuccess(SAVE_CARD_READER_VALUES, response?.data));
    } catch (e) {
      dispatch(actionFailure(SAVE_CARD_READER_VALUES, e));
    }
  };
  const updateGlobalCardReaderValues = async (
    values: PAYMENT_CONFIG.IGlobalCardReader
  ) => {
    dispatch(action(SAVE_CARD_READER_VALUES));
    try {
      const response = await apiWithEntityId.put(
        `/entityConfigurations`,
        values
      );
      dispatch(actionSuccess(SAVE_CARD_READER_VALUES, response?.data));
    } catch (e) {
      dispatch(actionFailure(SAVE_CARD_READER_VALUES, e));
    }
  };

  const setGlobalCardReaderDefault = async (
    values: PAYMENT_CONFIG.IGlobalCardReaderDefault
  ) => {
    dispatch(action(SAVE_DEFAULT_CARD_READER_VALUES));
    try {
      const response = await apiWithEntityId.post(`/defaultProcessors`, values);
      dispatch(actionSuccess(SAVE_DEFAULT_CARD_READER_VALUES, response?.data));
    } catch (e) {
      dispatch(actionFailure(SAVE_DEFAULT_CARD_READER_VALUES, e));
    }
  };

  const toGetPaymentDeviceLocation = async (processorName: string) => {
    dispatch(action(GET_PAYMENT_DEVICE));
    try {
      const response = await apiWithEntityId.get(
        `/paymentDeviceLocations/?processorNames=${processorName}&posLocationEnabled=true`
      );

      dispatch(actionSuccess(GET_PAYMENT_DEVICE, response?.data));
    } catch (e) {
      dispatch(actionFailure(GET_PAYMENT_DEVICE, e));
    }
  };

  const toGetPosLocation = async () => {
    dispatch(action(GET_POS_LOCATIONS));
    try {
      const url = `/locations/withPosLocations`; // `/paymentDeviceLocations/posLocations`;
      const response = await apiWithEntityId.get(url);
      dispatch(actionSuccess(GET_POS_LOCATIONS, response?.data));
    } catch (e) {
      dispatch(actionFailure(GET_POS_LOCATIONS, e));
    }
  };

  const toUpdatePaymentDeviceLocation = async (
    values:
      | PAYMENT_CONFIG.ICreatePaymentDeviceSimple
      | PAYMENT_CONFIG.ICreatePaymentDeviceFull,
    id: string
  ) => {
    dispatch(action(UPDATE_PAYMENT_DEVICE));
    try {
      const response = await apiWithEntityId.put(`/paymentDeviceLocations`, {
        ...values,
        id,
      });

      // The `put` wrapper resolves with either (a) the response body on 2xx,
      // or (b) the AxiosResponse on error (it swallows axios errors via
      // `.catch(err => err.response)`). Inspect both shapes to detect when
      // the backend has wrapped an upstream failure as a "successful" return.
      type FailureEnvelope = {
        message?: string;
        data?: unknown;
        errorRaw?: unknown;
      };
      const respUnknown = response as unknown as
        | (FailureEnvelope & { data?: FailureEnvelope | unknown })
        | undefined;
      const envelope: FailureEnvelope | undefined = respUnknown?.errorRaw
        ? respUnknown
        : respUnknown?.data && typeof respUnknown.data === 'object'
        ? (respUnknown.data as FailureEnvelope)
        : undefined;

      if (envelope?.errorRaw) {
        const errorMessage =
          envelope.message ||
          (typeof envelope.data === 'string' ? envelope.data : null) ||
          'Failed to update payment device';
        dispatch(
          actionFailure(UPDATE_PAYMENT_DEVICE, { message: errorMessage })
        );
        return;
      }

      dispatch(actionSuccess(UPDATE_PAYMENT_DEVICE, response));
    } catch (e) {
      const err = e as {
        response?: { data?: { message?: string; data?: unknown } };
        message?: string;
      };
      const errorData = err?.response?.data;
      const errorMessage =
        errorData?.message ||
        (typeof errorData?.data === 'string' ? errorData.data : null) ||
        err?.message ||
        'Failed to update payment device';
      dispatch(
        actionFailure(UPDATE_PAYMENT_DEVICE, { message: errorMessage })
      );
    }
  };
  const toDeletePaymentDeviceLocation = async (id: string) => {
    dispatch(action(DELETE_PAYMENT_DEVICE));
    try {
      const response = await apiWithEntityId.remove(
        `/paymentDeviceLocations/${id}`
      );
      dispatch(actionSuccess(DELETE_PAYMENT_DEVICE, response?.data));
    } catch (e) {
      dispatch(actionFailure(DELETE_PAYMENT_DEVICE, e));
    }
  };

  const toTestPaymentDeviceLocation = async (
    id: string,
    processorName: string
  ) => {
    dispatch(action(TEST_PAYMENT_DEVICE));
    try {
      const response = await apiWithEntityId.get(
        `/paymentDeviceLocations/${id}/status`,
        { processorName }
      );
      dispatch(actionSuccess(TEST_PAYMENT_DEVICE, response?.data));
    } catch (e) {
      dispatch(actionFailure(TEST_PAYMENT_DEVICE, e));
    }
  };

  const getCardReaderConfig = async (filter: string) => {
    dispatch(action(GET_CARD_READER_CONFIG));
    try {
      const response = await apiWithEntityId.get(
        `/processors/${filter}/configuration`
      );
      dispatch(actionSuccess(GET_CARD_READER_CONFIG, response?.data));
    } catch (e) {
      dispatch(actionFailure(GET_CARD_READER_CONFIG, e));
    }
  };

  const getPaymentStatus = async (paymentId?: string) => {
    dispatch(action(GET_PAYMENT_STATUS));
    try {
      const response = await apiWithEntityId.get(
        `/payments/${paymentId}/status`
      );
      dispatch(actionSuccess(GET_PAYMENT_STATUS, response?.data));
    } catch (e) {
      dispatch(actionFailure(GET_PAYMENT_STATUS, e));
    }
  };

  const getMSOPermissions = async () => {
    dispatch(action(GET_MSOPERMISSIONS));
    try {
      const response = await apiWithMSO.get(`/authorization/v1`);
      dispatch(actionSuccess(GET_MSOPERMISSIONS, response?.data));
    } catch (e) {
      dispatch(actionFailure(GET_MSOPERMISSIONS, e));
    }
  };

  const updatedFilterList = (list: PAYMENT_CONFIG.IProcessors[]) => {
    dispatch(actionSuccess(SET_FILTERED_LIST, list));
  };

  const resetDeviceCreated = () => {
    dispatch(action(RESET_DEVICE_CREATED));
  };
  const resetDeviceupdated = () => {
    dispatch(action(RESET_DEVICE_UPDATED));
  };

  const resetSaveCardReader = () => {
    dispatch(action(RESET_SAVE_CARD_READER_VALUES));
  };
  const resetSaveDefaultCardReader = () => {
    dispatch(action(RESET_SAVE_DEFAULT_CARD_READER_VALUES));
  };
  const resetCreateDefaultConfig = () => {
    dispatch(action(RESET_CREATE_DEFAULT_CONFIG));
  };
  const resetDeviceDelete = () => {
    dispatch(action(RESET_DEVICE_DELETED));
  };

  const resetCardReaderValues = () => {
    dispatch(action(RESET_CARD_READER_VALUES));
  };

  const resetPaymentDeviceList = () => {
    dispatch(action(RESET_PAYMENT_DEVICE_LIST));
  };

  const resetDeviceStatus = () => {
    dispatch(action(RESET_DEVICE_STATUS));
  };

  const toFetchChannelConfiguration = async (processorId: string) => {
    dispatch(action(FETCH_CHANNEL_CONFIGURATION));
    try {
      const response = await apiWithEntityId.get(
        `/channelConfiguration?processorId=${processorId}`
      );
      dispatch(actionSuccess(FETCH_CHANNEL_CONFIGURATION, response?.data));
    } catch (e) {
      dispatch(actionFailure(FETCH_CHANNEL_CONFIGURATION, e));
    }
  };

  const toUpdateChannelConfiguration = async (
    request: PAYMENT_CONFIG.IUpdateChannelConfigRequest
  ) => {
    dispatch(action(UPDATE_CHANNEL_CONFIGURATION));
    try {
      const response = await apiWithEntityId.post(
        `/channelConfiguration`,
        request
      );
      dispatch(actionSuccess(UPDATE_CHANNEL_CONFIGURATION, response?.data));
    } catch (e) {
      dispatch(actionFailure(UPDATE_CHANNEL_CONFIGURATION, e));
    }
  };

  const toFetchComponentSdkConfig = async ({
    component,
    entityId,
    namespace,
    channel,
  }: PAYMENT_CONFIG.IFetchComponentSdkConfigParams) => {
    dispatch(action(FETCH_COMPONENT_SDK_CONFIG));
    try {
      const response = await apiWithEntityId.get(
        `/config/${component}/${entityId}/${namespace}?channel=${channel}`
      );
      dispatch(actionSuccess(FETCH_COMPONENT_SDK_CONFIG, response?.data));
    } catch (e) {
      dispatch(actionFailure(FETCH_COMPONENT_SDK_CONFIG, e));
    }
  };

  const toUpdateComponentSdkConfig = async ({
    component,
    entityId,
    namespace,
    channel,
    schemaVersion,
    value,
  }: PAYMENT_CONFIG.IUpdateComponentSdkConfigParams) => {
    dispatch(action(UPDATE_COMPONENT_SDK_CONFIG));
    try {
      const response = await apiWithEntityId.put(
        `/config/${component}/${entityId}/${namespace}?channel=${channel}`,
        { schemaVersion, value }
      );
      dispatch(actionSuccess(UPDATE_COMPONENT_SDK_CONFIG, response));
    } catch (e) {
      dispatch(actionFailure(UPDATE_COMPONENT_SDK_CONFIG, e));
    }
  };

  return {
    paymentConfig,
    toFetchPaymentSolutions,
    toFetchAllPaymentSolutions,
    toUpdatePaymentSolutions,
    toDeletePaymentSolutions,
    toFetchDefaultProviders,
    toFetchDefaultConfigurations,
    toCreateDefaultConfiguration,
    toCreatePaymentDeviceLocation,
    toGetPaymentDeviceLocation,
    toGetPosLocation,
    toUpdatePaymentDeviceLocation,
    toTestPaymentDeviceLocation,
    updatedFilterList,
    resetDeviceCreated,
    resetDeviceupdated,
    toDeletePaymentDeviceLocation,
    resetDeviceDelete,
    toFetchCardReaderValues,
    saveGlobalCardReaderValues,
    updateGlobalCardReaderValues,
    setGlobalCardReaderDefault,
    resetSaveCardReader,
    resetSaveDefaultCardReader,
    resetCreateDefaultConfig,
    resetCardReaderValues,
    resetPaymentDeviceList,
    resetDeviceStatus,
    getCardReaderConfig,
    getPaymentStatus,
    getMSOPermissions,
    toFetchChannelConfiguration,
    toUpdateChannelConfiguration,
    toFetchComponentSdkConfig,
    toUpdateComponentSdkConfig,
  };
};

export default usePaymentConfig;
